import type { Vec3 } from '../types';

export function dist3(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Remaining route distance in FULL 3D — the single shared metric for HUD,
 * voice and arrival gating (INVARIANTS.md G1). Field bug (2026-07-11 21:16
 * session, "distance of kitchen is not 5 meters as i am in second floor"):
 * the HUD summed distXZ, so descending a storey displayed as ZERO extra
 * metres while voice spoke the route's true 3D length — two different
 * numbers for the same walk.
 *
 * `userFoot` must be the user's FOOT position (camera minus phone height):
 * passing the raw camera position inflates every reading by a constant
 * ~1 m of slant (hypot of phone height) and ruins the final metres.
 * `nextIdx` may be `nodes.length` (all waypoints consumed) — remaining is
 * then just foot → destination.
 */
export function remainingRouteM(
  userFoot: Vec3,
  nodes: ReadonlyArray<{ position: Vec3 }>,
  nextIdx: number,
  dest: Vec3,
): number {
  const target = nextIdx < nodes.length ? nodes[nextIdx].position : dest;
  let remaining = dist3(userFoot, target);
  for (let i = nextIdx + 1; i < nodes.length; i++) {
    remaining += dist3(nodes[i - 1].position, nodes[i].position);
  }
  if (nextIdx < nodes.length) {
    remaining += dist3(nodes[nodes.length - 1].position, dest);
  }
  return remaining;
}

/** Horizontal (XZ-plane) distance — better for arrival checks than full 3D. */
export function distXZ(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/** Clamped projection parameter t of point p onto segment ab in XZ. */
export function segClosestTXZ(p: Vec3, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const lenSq = abx * abx + abz * abz;
  return lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / lenSq));
}

/** Min lateral (XZ) distance from point p to segment ab (y ignored). */
export function pointToSegmentXZ(p: Vec3, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / lenSq));
  const cx = a.x + abx * t;
  const cz = a.z + abz * t;
  const dx = p.x - cx;
  const dz = p.z - cz;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * FLOOR-AWARE forward-segment search for the waypoint leapfrog (G4,
 * INVARIANTS.md). Returns the index of the best strictly-forward segment
 * (its END node becomes the new target), or -1 to keep the current leg.
 *
 * Floor gate — the reason this exists (issue #23, 2026-07-13 walk): with
 * the destination directly BELOW the start, the route's downstairs tail
 * passes under the user's feet, and an XZ-only search measured it 0.9 m
 * "away" THROUGH THE SLAB — target jumped node 2 → 15, arrow flicked,
 * false "Turn around" cues fired, HUD remaining collapsed 22 m → 5.4 m.
 * A segment is visible only when AT LEAST ONE endpoint is within
 * `floorTolM` of the user's foot height — so cross-floor stairs segments
 * stay eligible while actually descending them.
 */
export function nearestForwardSegmentIdx(
  user: Vec3,
  userFootY: number,
  pts: ReadonlyArray<Vec3>,
  nextIdx: number,
  floorTolM = 0.8,
  marginM = 0.15,
  maxLateralM = 3,
): number {
  if (pts.length < 2) return -1;
  const curIdx = Math.min(nextIdx, pts.length - 1);
  let bestD =
    nextIdx >= 1 ? pointToSegmentXZ(user, pts[nextIdx - 1], pts[curIdx]) : distXZ(user, pts[0]);
  let best = -1;
  for (let i = nextIdx; i < pts.length - 1; i++) {
    // Floor gate v2 (#23b, Test 3): endpoint checks let SWITCHBACK stairs
    // running back UNDER the corridor win (their upper run sits within
    // 1.5 m of the floor above). Gate on the segment's INTERPOLATED height
    // at the user's own XZ projection instead — on-floor and mid-descent
    // segments pass (the path under your feet is at your feet), while a
    // stair run one metre below the slab you're standing on does not.
    const t = segClosestTXZ(user, pts[i], pts[i + 1]);
    const yAtUser = pts[i].y + (pts[i + 1].y - pts[i].y) * t;
    if (Math.abs(yAtUser - userFootY) > floorTolM) continue; // another floor — invisible through the slab
    const d = pointToSegmentXZ(user, pts[i], pts[i + 1]);
    // Strictly-better wins (margin); ties keep the EARLIEST segment so we
    // never skip ahead along overlapping/parallel legs.
    if (d < bestD - marginM) {
      bestD = d;
      best = i;
    }
  }
  return best >= nextIdx && bestD < maxLateralM ? best : -1;
}

/**
 * G5 (INVARIANTS.md): latches the pose as SUSPECT while vertical motion is
 * physically implausible. Field evidence (Test 3, 2026-07-13): during a
 * VPS-blind stair descent, SLAM's height estimate dove ~5 m at ~2.4 m/s
 * (real stairs: ~0.5-0.7 m/s) — the corrupted pose spoke "Head UP the
 * stairs" and fired "You have arrived" 5 m early. While suspect, arrival /
 * floor cues / leapfrogging are suppressed; the latch clears only after
 * the height has been stable for a sustained window.
 */
export class VerticalRateMonitor {
  /** Sustained rate above this latches suspect (m/s). Stairs ≈ 0.7 max. */
  private static readonly SUSPECT_RATE = 1.8;
  /** Rate must exceed SUSPECT_RATE for this long to latch (one-frame tracking glitches are ignored). */
  private static readonly LATCH_MS = 250;
  /** Rate below this counts as stable (m/s). */
  private static readonly STABLE_RATE = 0.4;
  /** Stability must hold this long to clear the latch. */
  private static readonly CLEAR_MS = 2500;

  private lastY: number | null = null;
  private lastT = 0;
  private fastSince: number | null = null;
  private stableSince: number | null = null;
  private suspectFlag = false;

  /** Feed the user's FOOT height once per frame; returns current suspect state. */
  update(yFoot: number, tMs: number): boolean {
    if (this.lastY === null || tMs <= this.lastT) {
      this.lastY = yFoot;
      this.lastT = tMs;
      return this.suspectFlag;
    }
    const dt = (tMs - this.lastT) / 1000;
    const rate = Math.abs(yFoot - this.lastY) / dt;
    this.lastY = yFoot;
    this.lastT = tMs;

    if (!this.suspectFlag) {
      if (rate > VerticalRateMonitor.SUSPECT_RATE) {
        this.fastSince = this.fastSince ?? tMs;
        if (tMs - this.fastSince >= VerticalRateMonitor.LATCH_MS) {
          this.suspectFlag = true;
          this.stableSince = null;
        }
      } else {
        this.fastSince = null;
      }
    } else {
      if (rate < VerticalRateMonitor.STABLE_RATE) {
        this.stableSince = this.stableSince ?? tMs;
        if (tMs - this.stableSince >= VerticalRateMonitor.CLEAR_MS) {
          this.suspectFlag = false;
          this.fastSince = null;
        }
      } else {
        this.stableSince = null;
      }
    }
    return this.suspectFlag;
  }

  get suspect(): boolean {
    return this.suspectFlag;
  }
}

/**
 * G6 (INVARIANTS.md): a boolean that only reads true after its input has
 * been continuously true for `holdMs`. Used to debounce user-facing alerts
 * ("Turn around") that must never fire from a single-frame transient —
 * Tests 3+4 logged 4 false turn-around cues per walk, each during a pose
 * correction, while the user walked forward correctly.
 */
export class SustainedCondition {
  private since: number | null = null;

  constructor(private readonly holdMs: number) {}

  update(active: boolean, tMs: number): boolean {
    if (!active) {
      this.since = null;
      return false;
    }
    this.since = this.since ?? tMs;
    return tMs - this.since >= this.holdMs;
  }
}

/**
 * Whether the pose may be trusted to source a physical "turn around" command.
 * A turn-around asks the user to reverse — it must never be issued from a pose
 * the localizer itself is unsure about.
 *
 * Field issue #28 (Test 5, 2026-07-13): a single "Turn around" fired at
 * t=59.9 s while the anchor was CONTESTED — a Δ1.76 m correction was held at
 * t=53.4 s (agree 0/1) and never applied, and the very next fix wanted Δ5.41 m
 * (a 3.8 m snap landed 6 s later, proving the pose ~3.8 m wrong). None of the
 * earlier gate's flags (suspect / settling / recent-snap) were set, so the
 * cue slipped through. `holdingCorrection` closes that gap: while VPS keeps
 * returning a position that disagrees with the displayed anchor (a pending
 * hold), the heading the turn-around is computed from is not trustworthy.
 */
export interface PoseTrust {
  /** G5 vertical-rate suspect. */
  suspect: boolean;
  /** A correction is still gliding in (rate-capped ease). */
  settling: boolean;
  /** A snap-sized relocation landed within the last 6 s. */
  recentSnap: boolean;
  /** #28: an unconfirmed VPS correction is being held — the anchor is contested. */
  holdingCorrection: boolean;
}

export function poseTrustedForTurnAround(t: PoseTrust): boolean {
  return !t.suspect && !t.settling && !t.recentSnap && !t.holdingCorrection;
}

/**
 * G6 (INVARIANTS.md): the complete "Turn around" gate — 1.5 s SUSTAINED raw
 * misalignment AND a trusted pose (see {@link poseTrustedForTurnAround}).
 * Encapsulated here so the invariant is unit-testable without a camera/route
 * (NavigationOverlay wires the raw signal + live pose flags into it).
 */
export class TurnAroundDetector {
  private readonly hold: SustainedCondition;

  constructor(holdMs = 1500) {
    this.hold = new SustainedCondition(holdMs);
  }

  update(rawMisaligned: boolean, trust: PoseTrust, tMs: number): boolean {
    return this.hold.update(rawMisaligned && poseTrustedForTurnAround(trust), tMs);
  }
}

/**
 * #30 (Test 6): whether the pose is too uncertain to source an off-route
 * RECALCULATION. Recomputing a route from a position we already suspect is
 * wrong trashes a good route (comment in main.ts checkOffRoute).
 *
 * Field issue: at t=54.5 s an off-route recalc fired ("Route updated") while a
 * Δ4.54 m correction was HELD (t=53.2 s, unconfirmed) — the displayed anchor
 * disagreed with the localizer by metres. The existing guards covered a
 * *stale* pose (Relocalizing) and a *recent snap*, but not a held (pending)
 * correction, which is the same "position not trustworthy" condition (cf. #28
 * for turn-around). `holdingCorrection` closes that gap.
 */
export interface RecalcPoseGate {
  /** VPS lost / relocalizing — pose is stale. */
  localizationStale: boolean;
  /** A snap-sized relocation is still settling (within cooldown). */
  recentSnap: boolean;
  /** #30: an unconfirmed VPS correction is being held — the anchor is contested. */
  holdingCorrection: boolean;
}

export function recalcBlockedByPose(g: RecalcPoseGate): boolean {
  return g.localizationStale || g.recentSnap || g.holdingCorrection;
}

/**
 * Rate-limited angular slew toward a target heading (radians), used for the
 * guidance arrow's yaw (#29). An exponential ease (time constant `tauS`) gives
 * smooth tracking of gentle path curvature, while a hard max angular rate
 * guarantees the RENDERED heading can never flick: raw target-bearing
 * discontinuities — waypoint leapfrog, polyline rebuild on recalc, the
 * final-approach hand-off — swing the *target* by up to ~127° in one frame
 * (Test 6 logged 5 such raw jumps), but the arrow the user sees can only turn
 * at most `maxRateRadS · dt` per frame.
 *
 * Test 6 finding: the pre-existing exp-ease already absorbed these into a
 * ~0.6 s swing (video-verified smooth) — the "arrow jump … unexpected" log
 * lines measured the RAW bearing, not this rendered value, and cried wolf. The
 * cap makes the smoothness a guarantee rather than a tuning coincidence.
 */
export class HeadingSmoother {
  private yaw: number | null = null;

  constructor(
    private readonly tauS = 0.3,
    private readonly maxRateRadS = Math.PI, // 180°/s — a half-turn in 1 s
  ) {}

  /** Current rendered yaw, or null before the first update. */
  get value(): number | null {
    return this.yaw;
  }

  reset(): void {
    this.yaw = null;
  }

  update(targetRad: number, dtS: number): number {
    if (this.yaw === null) {
      this.yaw = targetRad;
      return this.yaw;
    }
    let d = targetRad - this.yaw;
    d = Math.atan2(Math.sin(d), Math.cos(d)); // shortest way round
    let step = d * (1 - Math.exp(-dtS / this.tauS));
    const maxStep = this.maxRateRadS * dtS;
    if (step > maxStep) step = maxStep;
    else if (step < -maxStep) step = -maxStep;
    this.yaw += step;
    return this.yaw;
  }
}

/**
 * #31 (Test 8): occluder chunk distance-cull budget (metres). Only scan-mesh
 * chunks whose bounding sphere is within this range of the user render their
 * (depth-only) pass; farther chunks are hidden even when in the frustum.
 *
 * Why 14 and not 25: AR content (crumbs, arrow, pin) is only ever visible
 * within the crumb reveal window — 8 m ahead, +3 m while settling, so ≤ 11 m —
 * plus a wall margin. A chunk beyond that occludes NOTHING the user can see. A
 * two-floor home is < 25 m across, so the old 25 m budget culled nothing: on
 * any open sightline the full 759k-tri scan drew every frame, dragging FPS to
 * 29–30 (video: 46 FPS at a near wall → 29 across open rooms). 14 m covers the
 * 11 m reveal window + margin while culling most of the house on open views.
 */
export const OCCLUDER_CULL_DIST_M = 14;

/**
 * Whether an occluder chunk at squared XZ distance `dxz2` (with bounding-sphere
 * `chunkRadius`) is close enough to matter. Pure so the cull budget is
 * unit-tested (a constant that silently grows back to 25 would re-break FPS).
 */
export function withinCullRadius(
  dxz2: number,
  chunkRadius: number,
  maxDist: number = OCCLUDER_CULL_DIST_M,
): boolean {
  const r = maxDist + chunkRadius;
  return dxz2 < r * r;
}

/**
 * #32 (Test 8, user: "why do I see breadcrumbs beyond a wall?"): the reveal
 * window shows crumbs up to `base` m (8) ahead, but the depth OCCLUDER that
 * hides crumbs behind real walls only finishes loading several seconds into a
 * session — the route is drawn at t≈15 s but "occluder ready" is t≈23 s. During
 * that ~8 s gap there is NO depth mask, so far crumbs render straight through
 * walls and cabinets (video: crumbs over the kitchen island before occluder
 * load). Until the occluder exists, clamp the ahead reveal to the near floor
 * the user can actually see (the always-visible arrow still shows direction);
 * once it's ready, the full window is safe because occlusion hides anything
 * behind a wall.
 */
export function crumbRevealAheadM(
  occluderReady: boolean,
  settling: boolean,
  base = 8,
  settleBonus = 3,
  preOccluder = 3.5,
): number {
  if (!occluderReady) return preOccluder;
  return settling ? base + settleBonus : base;
}

/**
 * Rolling FPS accumulator for the 🐞 export (Test 8 benchmark: the Mattercraft
 * reference shows a live "Frame rate 60 FPS" HUD; our on-screen HUD has FPS too
 * but the exported JSON did NOT — so FPS had to be read frame-by-frame off the
 * video). Feeding the SMOOTHED per-frame FPS gives a meaningful `min` (sustained
 * worst, not a one-frame spike), `avg`, and `last` for scoring §2.99 Cat 6.
 */
export class FpsStats {
  min: number | null = null;
  last: number | null = null;
  private sum = 0;
  private count = 0;

  add(fps: number): void {
    if (!(fps > 0)) return;
    this.last = Math.round(fps);
    this.sum += fps;
    this.count++;
    this.min = this.min === null ? fps : Math.min(this.min, fps);
  }

  get avg(): number | null {
    return this.count ? this.sum / this.count : null;
  }

  reset(): void {
    this.min = null;
    this.last = null;
    this.sum = 0;
    this.count = 0;
  }
}

/**
 * #34 (Test 9 baseline: FPS avg 37.7, min 22.1 — occluder still the cost after
 * the #31 cull). Occluder-mesh decimation grid (metres). Scan meshes carry a
 * huge number of sub-centimetre triangles; snapping each vertex to this grid
 * and dropping the ones that collapse to zero area (see `triAreaSq`) removes
 * that noise. It CANNOT hole a solid wall — a wall's large triangles survive
 * snapping; only detail finer than the grid merges away. Validated on-device by
 * the "occluder ready (Xk tris, decimated from Yk)" log.
 */
// Test 10: 4 cm alone cut only −15% (scan is redundant coplanar wall tris, not
// sub-cm noise) and FPS stayed flat → raised to 10 cm AND paired with post-snap
// triangle DEDUP (occluderService). 10 cm occlusion detail is imperceptible.
export const OCCLUDER_QUANT_CELL_M = 0.1;

/** Snap a coordinate to a grid. */
export function quantizeToCell(v: number, cell: number): number {
  return Math.round(v / cell) * cell;
}

/**
 * Squared area of triangle ABC. Used to drop occluder triangles that collapse
 * to (near) zero area after grid-snapping — i.e. anything smaller than the
 * grid. Squared to avoid a sqrt in the hot build loop.
 */
export function triAreaSq(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number {
  const ux = bx - ax, uy = by - ay, uz = bz - az;
  const vx = cx - ax, vy = cy - ay, vz = cz - az;
  const px = uy * vz - uz * vy;
  const py = uz * vx - ux * vz;
  const pz = ux * vy - uy * vx;
  return (px * px + py * py + pz * pz) / 4;
}

/**
 * Winding-independent key for a grid-snapped occluder triangle (#34 dedup):
 * three snapped vertices sorted so the same triangle in any vertex/winding
 * order maps to one key. Coordinates are exact multiples of the grid cell, so
 * string equality is safe.
 */
export function occluderTriKey(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): string {
  return [`${ax},${ay},${az}`, `${bx},${by},${bz}`, `${cx},${cy},${cz}`].sort().join('|');
}

export type TrailGuide = 'onscreen' | 'slight-left' | 'left' | 'slight-right' | 'right' | 'behind';

/**
 * #36 (user idea): make navigation DRIFT-TOLERANT. Instead of relying on the
 * breadcrumbs being pixel-perfectly registered (impossible under ~1.6 m SLAM
 * drift), bucket the signed bearing from the camera's forward direction to the
 * trail's next point (+ = the trail is to the user's RIGHT) and, when the trail
 * is off-screen, tell them which way to turn to bring it back into view. Coarse
 * buckets so drift can't flip left/right. `fovHalf` = half the camera's usable
 * horizontal field of view (deg): within it the crumbs are on screen.
 */
export function trailGuide(bearingDeg: number, fovHalf = 26): TrailGuide {
  const a = ((((bearingDeg % 360) + 540) % 360) - 180); // normalize to (-180, 180]
  const mag = Math.abs(a);
  if (mag <= fovHalf) return 'onscreen';
  if (mag >= 135) return 'behind';
  if (a < 0) return mag < 75 ? 'slight-left' : 'left';
  return mag < 75 ? 'slight-right' : 'right';
}

export function formatMetres(m: number): string {
  return m >= 10 ? `${Math.round(m)} m` : `${m.toFixed(1)} m`;
}

/**
 * Bearing from `a` to `b` in the map's own XZ frame, degrees, 0 = -Z
 * ("forward"), positive = clockwise toward +X. Matches the convention
 * NavigationOverlay.ts's arrow already uses (atan2(x, -z)-family), so the
 * compass-mode arrow (CompassOverlay.ts) stays consistent with the WebXR
 * arrow's sense of rotation.
 */
export function bearingDeg(a: Vec3, b: Vec3): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const rad = Math.atan2(dx, -dz);
  return normalizeAngleDeg((rad * 180) / Math.PI);
}

/** Normalizes an angle in degrees to [0, 360). */
export function normalizeAngleDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Shortest signed difference a -> b in degrees, result in (-180, 180]. */
export function angleDiffDeg(a: number, b: number): number {
  const diff = normalizeAngleDeg(b - a);
  return diff > 180 ? diff - 360 : diff;
}

/**
 * Rejects with `message` if `promise` hasn't settled within `ms`. Without
 * this, a hung fetch or a script that never fires its load event leaves the
 * UI silently stuck (see eightWallSession.ts's authorize() — this is what
 * turns an indefinite "Connecting to MultiSet…" freeze into a diagnosable
 * error).
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Smoothed FPS counter. Call tick() once per rendered frame (onFrame/onUpdate
 * callback); returns a rolling average so the on-screen number doesn't jitter
 * between individual frame times. Exponential moving average, alpha chosen
 * for ~1s settling time at 60fps.
 */
export class FpsMeter {
  private lastTime = 0;
  private smoothedFps = 0;

  tick(nowMs: number = performance.now()): number {
    if (this.lastTime > 0) {
      const deltaMs = nowMs - this.lastTime;
      if (deltaMs > 0) {
        const instantFps = 1000 / deltaMs;
        this.smoothedFps = this.smoothedFps === 0 ? instantFps : this.smoothedFps * 0.9 + instantFps * 0.1;
      }
    }
    this.lastTime = nowMs;
    return this.smoothedFps;
  }
}

/** Simple throttle for debug logging / UI updates. */
export function throttle<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let last = 0;
  return ((...args: never[]) => {
    const now = performance.now();
    if (now - last >= ms) {
      last = now;
      fn(...args);
    }
  }) as T;
}
